<template>
  <main class="public-shell">
    <section class="review-card">
      <img class="public-logo" src="@/assets/logo.svg" alt="Orator Pro" />

      <div v-if="loading" class="center-copy">Loading...</div>
      <div v-else-if="error" class="done-panel error-panel">
        <h1>Review unavailable</h1>
        <p>{{ error }}</p>
      </div>
      <div v-else-if="closed" class="done-panel">
        <h1>Collection has closed</h1>
        <p>Name pronunciation collection for this event is now closed.</p>
      </div>
      <div v-else-if="isFinalState" class="done-panel">
        <h1>{{ finalTitle }}</h1>
        <p>{{ finalCopy }}</p>
      </div>
      <div v-else>
        <p class="eyebrow">{{ eventName }}</p>
        <h1>Hi {{ studentName }}</h1>
        <p class="copy">
          Listen to how we plan to pronounce your name. If it sounds right, confirm it. If not, try a suggested correction or record a short correction.
        </p>

        <div class="audio-panel">
          <button class="secondary-button" type="button" @click="playGenerated" :disabled="playingGenerated">
            {{ playingGenerated ? "Playing..." : "Play generated pronunciation" }}
          </button>
        </div>

        <div v-if="mode === 'listen'" class="actions">
          <button class="primary-button" type="button" @click="confirmGenerated" :disabled="submitting">
            {{ submitting ? "Confirming..." : "Sounds right" }}
          </button>
          <button class="secondary-button" type="button" @click="mode = 'alternatives'">
            Not quite right
          </button>
        </div>

        <div v-if="mode === 'alternatives'" class="record-panel">
          <h2>Which part sounds wrong?</h2>
          <div class="part-list">
            <button
              v-for="part in pronunciationParts"
              :key="`${part.partText}-${part.phonetic}`"
              class="part-button"
              type="button"
              :class="{ active: selectedPart?.partText === part.partText }"
              @click="selectPart(part)"
            >
              <strong>{{ part.partText }}</strong>
              <span>{{ part.phonetic || "Generated pronunciation" }}</span>
            </button>
          </div>
          <div v-if="alternatives.length" class="alternative-list">
            <button
              v-for="(alternative, index) in alternatives"
              :key="alternative.key"
              class="alternative-button"
              type="button"
              :class="{ active: selectedAlternativeIndex === index }"
              @click="selectAlternative(alternative, index)"
            >
              <strong>{{ alternative.phonetic }}</strong>
            </button>
          </div>
          <div v-if="selectedAlternative?.fullPhonetics" class="full-name-preview">
            <span>Full name pronunciation</span>
            <strong>{{ selectedAlternative.fullPhonetics }}</strong>
          </div>
          <p v-if="alternativeError" class="error-copy">{{ alternativeError }}</p>
          <div class="actions">
            <button
              class="secondary-button"
              type="button"
              @click="previewSelectedAlternative"
              :disabled="!selectedAlternative || loadingAlternatives"
            >
              {{ loadingAlternatives ? "Loading..." : "Play full name" }}
            </button>
            <button
              class="primary-button"
              type="button"
              @click="submitAlternative"
              :disabled="!selectedAlternative || submitting"
            >
              {{ submitting ? "Submitting..." : "Submit correction" }}
            </button>
            <button class="secondary-button" type="button" @click="enterRecordingMode">
              Record instead
            </button>
          </div>
        </div>

        <div v-if="mode === 'record'" class="record-panel">
          <p class="copy">Say your name clearly. Recording stops automatically after 5 seconds.</p>
          <button
            v-if="recordState !== 'recording'"
            class="primary-button"
            type="button"
            @click="startRecording"
            :disabled="recordState === 'preparing'"
          >
            {{ recordState === "preparing" ? "Preparing..." : "Start recording" }}
          </button>
          <button v-else class="secondary-button danger" type="button" @click="stopRecording">
            Stop recording
          </button>
        </div>

        <div v-if="mode === 'preview'" class="record-panel">
          <h2>Check your recording</h2>
          <audio v-if="recordingUrl" class="audio-player" :src="recordingUrl" controls></audio>
          <div class="actions">
            <button class="primary-button" type="button" @click="submitRecording" :disabled="submitting">
              {{ submitting ? "Submitting..." : "Submit recording" }}
            </button>
            <button class="secondary-button" type="button" @click="recordAgain">
              Record again
            </button>
          </div>
        </div>
      </div>
    </section>
  </main>
</template>

<script>
import {
  confirmPublicGeneratedPronunciation,
  fetchPublicStudentPronunciation,
  fetchPublicStudentPronunciationAudioUrl,
  fetchPublicPronunciationAlternatives,
  previewPublicPronunciationAlternative,
  submitPublicPronunciationAlternative,
  submitPublicStudentRecording,
} from "@/lib/api";

export default {
  name: "PublicStudentReview",
  data() {
    return {
      loading: true,
      submitting: false,
      error: "",
      payload: null,
      mode: "listen",
      playingGenerated: false,
      selectedPart: null,
      alternatives: [],
      correctionAttemptId: "",
      selectedAlternative: null,
      selectedAlternativeIndex: -1,
      alternativeError: "",
      loadingAlternatives: false,
      recordState: "idle",
      mediaRecorder: null,
      recordingBlob: null,
      recordingBase64: "",
      recordingUrl: "",
      autoStopTimer: null,
    };
  },
  computed: {
    eventId() {
      return this.$route.params.eventId;
    },
    studentId() {
      return this.$route.params.studentId;
    },
    token() {
      return this.$route.params.token;
    },
    eventName() {
      return this.payload?.event?.name || "Graduation";
    },
    studentName() {
      return this.payload?.student?.fullName || "there";
    },
    status() {
      return this.payload?.student?.collection?.status || "not_started";
    },
    closed() {
      return Boolean(this.payload?.closed);
    },
    isFinalState() {
      return ["confirmed_generated", "selected_alternative", "needs_review", "approved", "rejected"].includes(this.status);
    },
    finalTitle() {
      if (this.status === "confirmed_generated") return "All set";
      if (this.status === "selected_alternative") return "Correction submitted";
      if (this.status === "needs_review") return "Recording submitted";
      if (this.status === "approved") return "Recording approved";
      if (this.status === "rejected") return "Recording reviewed";
      return "Thank you";
    },
    finalCopy() {
      if (this.status === "confirmed_generated") return "Thanks for confirming your name pronunciation.";
      if (this.status === "selected_alternative") return "Your selected correction has been sent to your university team for review.";
      if (this.status === "needs_review") return "Your university team will review your recording.";
      if (this.status === "approved") return "Your submitted pronunciation has been approved.";
      if (this.status === "rejected") return "Your university team has reviewed your recording.";
      return "Thanks for helping us get your name right.";
    },
    pronunciationParts() {
      const parts = this.payload?.student?.pronunciation?.parts || [];
      if (parts.length) return parts;
      return this.studentName
        .split(" ")
        .filter(Boolean)
        .map((partText) => ({ partText, phonetic: "", ipa: "" }));
    },
  },
  methods: {
    async load() {
      this.loading = true;
      this.error = "";
      try {
        this.payload = await fetchPublicStudentPronunciation(this.eventId, this.studentId, this.token);
      } catch (error) {
        this.error = "This review link could not be found.";
      } finally {
        this.loading = false;
      }
    },
    async playGenerated() {
      this.playingGenerated = true;
      this.error = "";
      try {
        const { url } = await fetchPublicStudentPronunciationAudioUrl(this.eventId, this.studentId, this.token);
        const audio = new Audio(url);
        audio.onended = () => {
          this.playingGenerated = false;
        };
        audio.onerror = () => {
          this.playingGenerated = false;
        };
        await audio.play();
      } catch (error) {
        this.playingGenerated = false;
        this.error = "Unable to play the generated pronunciation.";
      }
    },
    async confirmGenerated() {
      this.submitting = true;
      this.error = "";
      try {
        this.payload = await confirmPublicGeneratedPronunciation(this.eventId, this.studentId, this.token);
      } catch (error) {
        this.error = error?.response?.data?.error || "Unable to confirm pronunciation.";
      } finally {
        this.submitting = false;
      }
    },
    async selectPart(part) {
      this.selectedPart = part;
      this.alternatives = [];
      this.correctionAttemptId = "";
      this.selectedAlternative = null;
      this.selectedAlternativeIndex = -1;
      this.alternativeError = "";
      if (!part?.phonetic) {
        this.alternativeError = "Alternative suggestions need generated phonetics for this name part.";
        return;
      }
      this.loadingAlternatives = true;
      try {
        const result = await fetchPublicPronunciationAlternatives(this.eventId, this.studentId, this.token, {
          partText: part.partText,
          rejectedPhonetic: part.phonetic,
        });
        this.correctionAttemptId = result.correctionAttemptId || "";
        this.alternatives = (result.alternatives || []).map((alternative, index) => ({
          key: `${index}:${alternative.phonetic || ""}:${alternative.ipa || ""}`,
          phonetic: alternative.phonetic || "",
          ipa: alternative.ipa || "",
          fullPhonetics: alternative.fullPhonetics || "",
        }));
        if (!this.alternatives.length) {
          this.alternativeError = "No alternative suggestions are available for this part.";
        }
      } catch (error) {
        this.alternativeError = error?.response?.data?.error || "Unable to load alternative pronunciations.";
      } finally {
        this.loadingAlternatives = false;
      }
    },
    selectAlternative(alternative, index) {
      this.selectedAlternative = alternative;
      this.selectedAlternativeIndex = index;
      this.alternativeError = "";
    },
    enterRecordingMode() {
      this.mode = "record";
      this.alternativeError = "";
    },
    async previewSelectedAlternative() {
      if (!this.selectedAlternative) return;
      this.loadingAlternatives = true;
      this.alternativeError = "";
      try {
        const result = await previewPublicPronunciationAlternative(this.eventId, this.studentId, this.token, {
          partText: this.selectedPart?.partText || "",
          phonetic: this.selectedAlternative.phonetic,
          ipa: this.selectedAlternative.ipa,
          correctionAttemptId: this.correctionAttemptId,
        });
        const audio = new Audio(`data:${result.audioContentType};base64,${result.audioBase64}`);
        await audio.play();
      } catch (error) {
        this.alternativeError = error?.response?.data?.error || "Unable to play this alternative.";
      } finally {
        this.loadingAlternatives = false;
      }
    },
    async submitAlternative() {
      if (!this.selectedAlternative) return;
      this.submitting = true;
      this.alternativeError = "";
      try {
        this.payload = await submitPublicPronunciationAlternative(this.eventId, this.studentId, this.token, {
          partText: this.selectedPart?.partText || "",
          phonetic: this.selectedAlternative.phonetic,
          ipa: this.selectedAlternative.ipa,
          correctionAttemptId: this.correctionAttemptId,
        });
      } catch (error) {
        this.alternativeError = error?.response?.data?.error || "Unable to submit this alternative.";
      } finally {
        this.submitting = false;
      }
    },
    async startRecording() {
      this.recordState = "preparing";
      this.error = "";
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        const recorder = new MediaRecorder(stream, {
          audioBitsPerSecond: 64000,
          mimeType: "audio/webm",
        });
        const chunks = [];
        recorder.ondataavailable = (event) => {
          if (event.data?.size) chunks.push(event.data);
        };
        recorder.onstop = async () => {
          stream.getTracks().forEach((track) => track.stop());
          this.recordingBlob = new Blob(chunks, { type: "audio/webm" });
          this.recordingUrl = URL.createObjectURL(this.recordingBlob);
          const buffer = await this.recordingBlob.arrayBuffer();
          this.recordingBase64 = this.arrayBufferToBase64(buffer);
          this.mode = "preview";
          this.recordState = "idle";
        };
        recorder.start();
        this.mediaRecorder = recorder;
        this.recordState = "recording";
        this.autoStopTimer = window.setTimeout(() => this.stopRecording(), 5000);
      } catch (error) {
        this.recordState = "idle";
        this.error = "Unable to access your microphone. Check browser permissions and try again.";
      }
    },
    stopRecording() {
      if (this.autoStopTimer) {
        window.clearTimeout(this.autoStopTimer);
        this.autoStopTimer = null;
      }
      if (this.mediaRecorder && this.mediaRecorder.state !== "inactive") {
        this.mediaRecorder.stop();
      }
    },
    recordAgain() {
      if (this.recordingUrl) URL.revokeObjectURL(this.recordingUrl);
      this.recordingBlob = null;
      this.recordingBase64 = "";
      this.recordingUrl = "";
      this.mode = "record";
    },
    async submitRecording() {
      this.submitting = true;
      this.error = "";
      try {
        this.payload = await submitPublicStudentRecording(this.eventId, this.studentId, this.token, {
          audioBase64: this.recordingBase64,
          audioContentType: "audio/webm",
        });
      } catch (error) {
        this.error = error?.response?.data?.error || "Unable to submit recording.";
      } finally {
        this.submitting = false;
      }
    },
    arrayBufferToBase64(buffer) {
      let binary = "";
      const bytes = new Uint8Array(buffer);
      bytes.forEach((byte) => {
        binary += String.fromCharCode(byte);
      });
      return window.btoa(binary);
    },
  },
  mounted() {
    this.load();
  },
  beforeUnmount() {
    if (this.autoStopTimer) window.clearTimeout(this.autoStopTimer);
    if (this.mediaRecorder && this.mediaRecorder.state !== "inactive") {
      this.mediaRecorder.stop();
    }
    if (this.recordingUrl) URL.revokeObjectURL(this.recordingUrl);
  },
};
</script>

<style scoped>
.public-shell {
  min-height: 100vh;
  display: grid;
  place-items: center;
  padding: 1.5rem;
  background: var(--orator-light);
}

.review-card {
  width: min(100%, 560px);
  background: white;
  border: 1px solid var(--orator-smoke);
  border-radius: 0.75rem;
  box-shadow: var(--orator-shadow);
  padding: 2rem;
}

.public-logo {
  width: 170px;
  margin-bottom: 2rem;
}

.eyebrow {
  margin: 0 0 0.5rem;
  color: var(--orator-blue);
  font-weight: 800;
  text-transform: uppercase;
  font-size: 0.75rem;
}

h1 {
  margin: 0;
  color: var(--orator-navy);
  font-size: 2rem;
  line-height: 1.1;
}

h2 {
  color: var(--orator-navy);
}

.copy,
.center-copy,
.done-panel p {
  color: var(--orator-graphite);
  line-height: 1.55;
}

.audio-panel,
.record-panel,
.done-panel {
  margin-top: 1.5rem;
  background: var(--orator-cloud);
  border-radius: 0.5rem;
  padding: 1rem;
}

.part-list,
.alternative-list {
  display: grid;
  gap: 0.65rem;
  margin-top: 1rem;
}

.full-name-preview {
  display: grid;
  gap: 0.3rem;
  margin-top: 1rem;
  padding: 0.85rem;
  border-left: 3px solid var(--orator-blue);
  background: white;
  color: var(--orator-navy);
}

.full-name-preview span {
  color: var(--orator-graphite);
  font-size: 0.78rem;
  font-weight: 800;
  text-transform: uppercase;
}

.full-name-preview strong {
  overflow-wrap: anywhere;
}

.part-button,
.alternative-button {
  border: 1px solid var(--orator-steel);
  background: white;
  border-radius: 0.5rem;
  padding: 0.85rem;
  text-align: left;
  color: var(--orator-navy);
}

.part-button strong,
.part-button span,
.alternative-button strong {
  display: block;
}

.part-button span {
  margin-top: 0.25rem;
  color: var(--orator-graphite);
  font-size: 0.9rem;
}

.part-button.active,
.alternative-button.active {
  border-color: var(--orator-blue);
  box-shadow: inset 0 0 0 1px var(--orator-blue);
}

.actions {
  display: flex;
  gap: 0.75rem;
  margin-top: 1rem;
  flex-wrap: wrap;
}

.primary-button,
.secondary-button {
  border-radius: 0.5rem;
  padding: 0.85rem 1rem;
  font-weight: 800;
  border: 1px solid var(--orator-blue);
}

.primary-button {
  background: var(--orator-blue);
  color: white;
}

.secondary-button {
  background: white;
  color: var(--orator-navy);
}

.secondary-button.danger {
  border-color: #b42318;
  color: #b42318;
}

button:disabled {
  opacity: 0.65;
}

.audio-player {
  width: 100%;
}

.error-panel {
  background: #fff1f0;
}
</style>
